import { useEffect } from "react";
import { Activity, AlertTriangle, ChevronLeft, ChevronRight, Presentation, Scissors, TrendingUp, X } from "lucide-react";
import { Button } from "@/components/ui/button";

import PresentationSlide from "./PresentationSlide";
import { Kpi } from "./widgets";
import { SLIDES, brl, num } from "./utils";

/**
 * Full-screen "Modo reunião" overlay. Cycles through the slides; ESC and
 * arrow keys are handled here so the parent doesn't have to.
 */
export default function PresentationOverlay({
  open, onClose, activeTab, setActiveTab,
  data, totals, monthSeries, pieShift, pieSusp, weekdaySeries,
  topSurgeons, topProcs, topAnesth, topHospitals,
  filters,
}) {
  const slideIdx = SLIDES.findIndex((s) => s.key === activeTab);

  function nextSlide() {
    setActiveTab(SLIDES[Math.min(SLIDES.length - 1, slideIdx + 1)].key);
  }
  function prevSlide() {
    setActiveTab(SLIDES[Math.max(0, slideIdx - 1)].key);
  }

  useEffect(() => {
    if (!open) return;
    function onKey(e) {
      if (e.key === "ArrowRight" || e.key === "PageDown" || e.key === " ") {
        e.preventDefault(); nextSlide();
      } else if (e.key === "ArrowLeft" || e.key === "PageUp") {
        e.preventDefault(); prevSlide();
      } else if (e.key === "Escape") {
        onClose();
      }
    }
    function onFsChange() {
      if (!document.fullscreenElement && open) onClose();
    }
    window.addEventListener("keydown", onKey);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("fullscreenchange", onFsChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, slideIdx]);

  if (!open) return null;
  const t = totals;

  return (
    <div
      className="fixed inset-0 z-[100] bg-white overflow-hidden flex flex-col"
      data-testid="presentation-overlay"
    >
      <div className="flex items-center justify-between px-8 py-4 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-md bg-blue-700 flex items-center justify-center">
            <Presentation className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="font-display text-xl font-bold text-slate-900">{SLIDES[slideIdx]?.label}</div>
            <div className="text-xs text-slate-500">
              BV Serviços Médicos · {new Date().toLocaleDateString("pt-BR")}
              {(filters.date_from || filters.date_to) && (
                <> · período {filters.date_from || "∞"} a {filters.date_to || "hoje"}</>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-sm text-slate-500 font-mono">
            {slideIdx + 1} / {SLIDES.length}
          </div>
          <Button variant="outline" size="sm" onClick={onClose} data-testid="btn-presentation-exit">
            <X className="h-4 w-4 mr-1" /> Sair (ESC)
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-auto px-10 py-8">
        <div className="max-w-[1800px] mx-auto">
          <div className="grid grid-cols-4 lg:grid-cols-8 gap-3 mb-8">
            <Kpi icon={Activity} tone="blue" label="Casos" value={num(t.cases_total)} />
            <Kpi icon={Scissors} tone="blue" label="Procedimentos" value={num(t.procedures_performed)} />
            <Kpi icon={TrendingUp} tone="green" label="Executadas" value={num(t.cases_executed)} />
            <Kpi icon={AlertTriangle} tone="amber" label="Suspensas" value={num(t.cases_suspended)} />
            <Kpi icon={TrendingUp} tone="slate" label="Taxa exec." value={`${t.execution_rate ?? 0}%`} />
            <Kpi icon={TrendingUp} tone="slate" label="Aprovado" value={brl(t.approved)} />
            <Kpi icon={TrendingUp} tone="blue" label="Faturado" value={brl(t.billed)} />
            <Kpi icon={TrendingUp} tone="green" label="Recebido" value={brl(t.received)} />
          </div>

          <PresentationSlide
            slide={activeTab}
            data={data}
            monthSeries={monthSeries}
            pieShift={pieShift}
            pieSusp={pieSusp}
            weekdaySeries={weekdaySeries}
            topSurgeons={topSurgeons}
            topProcs={topProcs}
            topAnesth={topAnesth}
            topHospitals={topHospitals}
            totals={t}
          />
        </div>
      </div>

      <div className="flex items-center justify-between px-8 py-4 border-t border-slate-200 bg-slate-50">
        <Button
          variant="outline"
          onClick={prevSlide}
          disabled={slideIdx === 0}
          data-testid="btn-slide-prev"
        >
          <ChevronLeft className="h-4 w-4 mr-1" /> Anterior
        </Button>
        <div className="flex gap-1.5">
          {SLIDES.map((s, i) => (
            <button
              key={s.key}
              onClick={() => setActiveTab(s.key)}
              className={`h-2 rounded-full transition-all ${i === slideIdx ? "w-8 bg-blue-700" : "w-2 bg-slate-300 hover:bg-slate-400"}`}
              title={s.label}
              data-testid={`slide-dot-${s.key}`}
            />
          ))}
        </div>
        <Button
          onClick={nextSlide}
          disabled={slideIdx === SLIDES.length - 1}
          className="bg-blue-700 hover:bg-blue-800"
          data-testid="btn-slide-next"
        >
          Próximo <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>

      <div className="absolute bottom-20 left-1/2 -translate-x-1/2 text-[10px] text-slate-400 tracking-wider uppercase pointer-events-none">
        Use ←  →  ESC · ou as setas do teclado
      </div>
    </div>
  );
}
